import { useCallback, useRef, useState } from "react";
import { IRecommendationsVariants, ISynonyms } from "types/subscribtios";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { IWord } from "../components/EditBox/types";
import { useAudioStateContext } from "../../../../../../hooks/useAudioStateContext";
import { MetadataToSubtitlesLanguages } from "../../../../../../providers/AudioProvider/types";

export interface IUseWords {
  variant?: IRecommendationsVariants | null;
  setActiveWordId: (id: string | null) => void;
  subtitleId: string | null;
}

export const useWords = ({ setActiveWordId, variant, subtitleId }: IUseWords) => {
  const handles = useAudioHandlesContext();
  const state = useAudioStateContext();
  const [words, setWords] = useState<IWord[]>([]);
  const defaultWords = useRef<IWord[]>([]);

  const addWord = useCallback((word: IWord) => {
    setWords((prev) => [...prev, word]);
  }, []);

  const addWords = useCallback((words: IWord[]) => {
    setWords((prev) => [...prev, ...words]);
  }, []);

  const moveWord = useCallback(
    (from: number, to: number) => {
      const updated = [...words];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      setWords(updated);
      setActiveWordId(null);
    },
    [words, setActiveWordId],
  );

  const updateWord = useCallback(
    (index: number, newText: string) => {
      const updated = [...words];
      updated[index] = { ...updated[index], text: newText };
      setWords(updated);
      setActiveWordId(null);
    },
    [words],
  );

  const deleteWord = useCallback(
    (index: number) => {
      const updated = words.filter((_, i) => i !== index);
      setWords(updated);
      setActiveWordId(null);
    },
    [words],
  );

  const save = useCallback(
    (newWords: IWord[]) => {
      if (subtitleId && newWords.length) {
        void handles.editTranslatedSubtitle({
          id: subtitleId,
          captions: newWords.map((item) => item.text).join(" "),
          lang: MetadataToSubtitlesLanguages[state.lang],
          update_recommendation: false,
        });
      }
    },
    [subtitleId],
  );

  const reset = useCallback(() => {
    if (variant) {
      setWords(defaultWords.current);
    } else {
      setWords([]);
    }
    setActiveWordId(null);
  }, [words]);

  return { words, moveWord, updateWord, addWord, reset, save, deleteWord, addWords };
};
